import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Papa from 'papaparse';

function cleanCell(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === '< Skip Field >') return null;
  return trimmed;
}

function parseDate(value) {
  const v = cleanCell(value);
  if (!v) return null;
  const us = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) {
    const [, m, d, y] = us;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  const d = new Date(v);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return null;
}

function parseNumeric(value) {
  const v = cleanCell(value);
  if (!v) return null;
  const n = Number(v.replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function buildRowMapper({ mapping, dateFields, numericFields, valueTransforms, defaults }) {
  const dateSet = new Set(dateFields || []);
  const numericSet = new Set(numericFields || []);
  const transforms = valueTransforms || {};
  return function mapRow(rawRow) {
    const mapped = { ...(defaults || {}) };
    for (const [csvCol, dbField] of Object.entries(mapping)) {
      const raw = rawRow[csvCol];
      let value;
      if (dateSet.has(dbField)) value = parseDate(raw);
      else if (numericSet.has(dbField)) value = parseNumeric(raw);
      else value = cleanCell(raw);
      if (value != null && transforms[dbField]) value = transforms[dbField](value);
      mapped[dbField] = value;
    }
    return mapped;
  };
}

export default function CsvImporter({
  title,
  subtitle,
  backTo,
  backLabel,
  mapping,
  dateFields = [],
  numericFields = [],
  valueTransforms,
  defaults,
  previewColumns,
  requiredField = 'last_name',
  requiredLabel = 'last name',
  apiPath,
  successCta = 'View list',
  successHint
}) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const mapRow = useMemo(
    () => buildRowMapper({ mapping, dateFields, numericFields, valueTransforms, defaults }),
    [mapping, dateFields, numericFields, valueTransforms, defaults]
  );

  const matched = useMemo(() => headers.filter((h) => mapping[h]), [headers, mapping]);
  const ignored = useMemo(
    () => headers.filter((h) => !mapping[h] && h && h !== '< Skip Field >'),
    [headers, mapping]
  );
  const validRows = useMemo(
    () => rows.filter((r) => r[requiredField] && String(r[requiredField]).trim()),
    [rows, requiredField]
  );
  const invalidCount = rows.length - validRows.length;

  function handleFile(file) {
    if (!file) return;
    setParseError(null);
    setResult(null);
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      complete: (results) => {
        if (results.errors && results.errors.length) {
          const firstErr = results.errors[0];
          setParseError(`Parse error on row ${firstErr.row}: ${firstErr.message}`);
        }
        setHeaders(results.meta.fields || []);
        setRows(results.data.map(mapRow));
      },
      error: (err) => setParseError(err.message)
    });
  }

  function onDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  async function handleImport() {
    setImporting(true);
    setResult(null);
    try {
      const res = await axios.post(apiPath, { rows: validRows, source_filename: fileName });
      setResult(res.data);
    } catch (err) {
      setParseError(err.response?.data?.error || err.message);
    } finally {
      setImporting(false);
    }
  }

  function reset() {
    setFileName('');
    setHeaders([]);
    setRows([]);
    setResult(null);
    setParseError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  return (
    <section className="page">
      <div className="page-header">
        <div>
          <h2>{title}</h2>
          {subtitle && <div className="subtitle">{subtitle}</div>}
        </div>
        <Link to={backTo} className="btn-secondary">← {backLabel}</Link>
      </div>

      {parseError && <p className="error">{parseError}</p>}

      {result && (
        <div className="import-result">
          <h3>Import Complete</h3>
          <p>
            Imported <strong>{result.inserted_count}</strong> record{result.inserted_count === 1 ? '' : 's'}.
            {result.skipped_count > 0 && <> Skipped <strong>{result.skipped_count}</strong> row{result.skipped_count === 1 ? '' : 's'}.</>}
          </p>
          {result.skipped_count > 0 && (
            <details>
              <summary>Skipped rows ({result.skipped_count})</summary>
              <ul className="skipped-list">
                {result.skipped.map((s) => (
                  <li key={s.index}>Row {s.index + 2}: {s.reason}</li>
                ))}
              </ul>
            </details>
          )}
          <div className="form-actions" style={{ marginTop: 16 }}>
            <button type="button" className="btn-secondary" onClick={reset}>Import another file</button>
            <button type="button" className="btn-primary" onClick={() => navigate(backTo)}>
              {successCta}
            </button>
          </div>
        </div>
      )}

      {!result && rows.length === 0 && (
        <div
          className="drop-zone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          <div className="drop-icon">⇪</div>
          <div className="drop-primary">Drop a CSV file here, or click to choose</div>
          <div className="drop-secondary">Maximizer export format — first row must contain column headers</div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
      )}

      {!result && rows.length > 0 && (
        <>
          <div className="import-summary">
            <div className="summary-card">
              <div className="summary-label">File</div>
              <div className="summary-value cell-mono">{fileName}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Rows</div>
              <div className="summary-value">
                {rows.length}
                {invalidCount > 0 && (
                  <span className="summary-warn"> · {invalidCount} skip (no {requiredLabel})</span>
                )}
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Mapped columns</div>
              <div className="summary-value">{matched.length} / {headers.length}</div>
            </div>
          </div>

          <details className="mapping-detail" open>
            <summary>Column mapping</summary>
            <div className="mapping-grid">
              {matched.map((h) => (
                <div key={h} className="mapping-row">
                  <span className="mapping-csv">{h}</span>
                  <span className="mapping-arrow">→</span>
                  <span className="mapping-db cell-mono">{mapping[h]}</span>
                </div>
              ))}
            </div>
            {ignored.length > 0 && (
              <div className="mapping-ignored">
                <strong>Ignored:</strong> {ignored.join(', ')}
              </div>
            )}
          </details>

          <h3 className="preview-heading">Preview (first 5 rows)</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  {previewColumns.map((c) => <th key={c}>{c.replace(/_/g, ' ')}</th>)}
                </tr>
              </thead>
              <tbody>
                {validRows.slice(0, 5).map((r, idx) => (
                  <tr key={idx}>
                    {previewColumns.map((c) => {
                      const isMono = dateFields.includes(c) || numericFields.includes(c) || c === 'site_number' || c === 'phone_main';
                      return (
                        <td key={c} className={isMono ? 'cell-mono' : ''}>
                          {r[c] ?? <span className="cell-muted">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="form-actions" style={{ marginTop: 20 }}>
            {successHint && <span className="form-hint">{successHint}</span>}
            <button type="button" className="btn-secondary" onClick={reset} disabled={importing}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={handleImport} disabled={importing || validRows.length === 0}>
              {importing ? 'Importing…' : `Import ${validRows.length} record${validRows.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
